import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  CoverageAmountType,
  RoundingRule,
} from "@shared/schema";
import type { LifeDisabilityCoverageConfig, InsertLifeDisabilityCoverageConfig } from "@shared/schema";
import { useToast } from "@/hooks/use-toast";
import { getLifeDisabilityRateDefaults } from "@/lib/lifeDisabilityRateDefaults";

import { AmountsTab } from "./lifeDisability/AmountsTab";
import { AgeReductionTab } from "./lifeDisability/AgeReductionTab";
import type { AgeReductionTabHandle } from "./lifeDisability/AgeReductionTab";
import { RoundingTab } from "./lifeDisability/RoundingTab";
import { GuaranteedIssueTab } from "./lifeDisability/GuaranteedIssueTab";
import { DisabilityTab } from "./lifeDisability/DisabilityTab";
import { AdditionalFeaturesTab } from "./lifeDisability/AdditionalFeaturesTab";

interface LifeDisabilityConfigFormProps {
  planId: number;
  planType: "life" | "add" | "voluntary_life" | "disability" | "std" | "ltd";
  config?: LifeDisabilityCoverageConfig;
  onSave?: (config: InsertLifeDisabilityCoverageConfig) => void;
  onCancel?: () => void;
}

function getDefaultsForPlanType(planType: string): Partial<InsertLifeDisabilityCoverageConfig> {
  const rate = getLifeDisabilityRateDefaults(planType);
  switch (planType) {
    case "life":
    case "add":
      return {
        coverageAmountType: CoverageAmountType.EARNINGS_MULTIPLE,
        earningsMultiple: "2",
        roundingRule: RoundingRule.NEAREST_THOUSAND,
        ...rate,
      };
    case "voluntary_life":
      return {
        coverageAmountType: CoverageAmountType.INCREMENTS,
        roundingRule: RoundingRule.NEAREST_THOUSAND,
        ...rate,
      };
    case "std":
      return {
        coverageAmountType: CoverageAmountType.EARNINGS_PERCENTAGE,
        earningsPercentage: "60",
        eliminationPeriod: "7_days",
        benefitSchedule: "weekly",
        roundingRule: RoundingRule.NEAREST_HUNDRED,
        ...rate,
      };
    case "ltd":
      return {
        coverageAmountType: CoverageAmountType.EARNINGS_PERCENTAGE,
        earningsPercentage: "60",
        eliminationPeriod: "90_days",
        benefitSchedule: "monthly",
        roundingRule: RoundingRule.NEAREST_HUNDRED,
        ...rate,
      };
    default:
      return {
        coverageAmountType: CoverageAmountType.FLAT_AMOUNT,
        roundingRule: RoundingRule.NONE,
        ...rate,
      };
  }
}

export function LifeDisabilityConfigForm({
  planId,
  planType,
  config,
  onSave,
  onCancel,
}: LifeDisabilityConfigFormProps) {
  const defaults = getDefaultsForPlanType(planType);

  const { data: existingConfig } = useQuery({
    queryKey: [`/api/plans/${planId}/life-disability-config`],
    queryFn: async () => {
      const res = await fetch(`/api/plans/${planId}/life-disability-config`);
      if (!res.ok) return null;
      return res.json();
    },
  });

  const [formData, setFormData] = useState<Partial<InsertLifeDisabilityCoverageConfig>>({
    planId,
    eoiRequired: false,
    portabilityAllowed: false,
    conversionAllowed: false,
    acceleratedDeathBenefit: false,
    ...defaults,
    ...config,
  });

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const savingRef = useRef(false);
  const ageReductionRef = useRef<AgeReductionTabHandle>(null);

  useEffect(() => {
    const source = config || existingConfig;
    if (source) {
      setFormData((prev) => ({ ...prev, ...source, planId }));
    }
  }, [config, existingConfig, planId]);

  const mutation = useMutation({
    mutationFn: async () => {
      const cleanedData = Object.fromEntries(
        Object.entries(formData).filter(([_, v]) => v !== "")
      );
      const endpoint = `/api/plans/${planId}/life-disability-config/upsert`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(cleanedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save configuration");
      }

      // Also save age reduction schedules
      await ageReductionRef.current?.saveSchedules();

      return response.json();
    },
    onSuccess: (savedConfig) => {
      queryClient.invalidateQueries({ queryKey: [`/api/plans/${planId}/life-disability-config`] });
      toast({ title: "Saved", description: "Coverage configuration saved successfully." });
      onSave?.(savedConfig);
    },
    onSettled: () => {
      savingRef.current = false;
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (savingRef.current) return;
    savingRef.current = true;
    mutation.mutate();
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const isDisability = planType === "disability" || planType === "std" || planType === "ltd";
  const isLife = planType === "life" || planType === "add" || planType === "voluntary_life";

  const saveButton = (
    <div className="flex justify-end pt-4">
      <Button type="submit" size="sm" disabled={mutation.isPending}>
        {mutation.isPending ? "Saving..." : "Save"}
      </Button>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {mutation.error && (
        <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded">
          {mutation.error.message}
        </div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Amounts</CardTitle>
        </CardHeader>
        <CardContent>
          <AmountsTab formData={formData} updateField={updateField} planType={planType} />
          {saveButton}
        </CardContent>
      </Card>

      {isDisability && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Disability</CardTitle>
          </CardHeader>
          <CardContent>
            <DisabilityTab formData={formData} updateField={updateField} planType={planType} />
            {saveButton}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>GI / EOI</CardTitle>
        </CardHeader>
        <CardContent>
          <GuaranteedIssueTab formData={formData} updateField={updateField} planType={planType} />
          {saveButton}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Rounding</CardTitle>
        </CardHeader>
        <CardContent>
          <RoundingTab formData={formData} updateField={updateField} planType={planType} />
          {saveButton}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Age Reduction</CardTitle>
        </CardHeader>
        <CardContent>
          <AgeReductionTab ref={ageReductionRef} formData={formData} updateField={updateField} planType={planType} planId={planId} />
          {saveButton}
        </CardContent>
      </Card>

      {isLife && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Features</CardTitle>
          </CardHeader>
          <CardContent>
            <AdditionalFeaturesTab formData={formData} updateField={updateField} planType={planType} />
            {saveButton}
          </CardContent>
        </Card>
      )}
    </form>
  );
}
